import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Tooltip } from '@mui/material';
import { RestartAltOutlined } from '@mui/icons-material';
import { fontSize } from '../../../';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { groupProp } from '../../../redux/ChartFilterGroup/ChartFilterGroupInterface';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { useDispatch } from 'react-redux';
import {
  updateChartFilterGroupsCollapsed,
  updateChartFilterGroupsName,
} from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import Logger from '../../../Logger';
import { AlertColor } from '@mui/material/Alert';
import { getAllFilterOptions } from './getAllFilterOptions';

interface Props {
  group: groupProp;
  fromDashboard: boolean;
  setSeverity: React.Dispatch<React.SetStateAction<AlertColor>>;
  setOpenAlert: React.Dispatch<React.SetStateAction<boolean>>;
  setTestMessage: React.Dispatch<React.SetStateAction<string>>;
  anchorEl: HTMLButtonElement | null;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPopover: React.Dispatch<React.SetStateAction<boolean>>;
  setAnchorElSyncFilter: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;

  color?: string;
}

/**
 * rendered by ChartFilterGroups.tsx
 * @returns group name with edit functionality, a more icon which is responsible to open a menu/popover and a expand/collpase icon.
 */
const GroupHeader = ({
  group,
  fromDashboard,
  setSeverity,
  setOpenAlert,
  setTestMessage,
  anchorEl,
  setAnchorEl,
  setLoading,
  setShowPopover,
  setAnchorElSyncFilter,
  color,
}: Props) => {
  const { tabTileProps, chartGroup, dashBoardFilterGroup, token } = useSelector(
    (state: RootState) => ({
      tabTileProps: state.tabTileProps,
      chartGroup: state.chartFilterGroup,
      dashBoardFilterGroup: state.dashBoardFilterGroup,
      token: state.isLogged.accessToken,
    }),
  );

  const dsDetails = tabTileProps.selectedDataSetList.find(
    (dataset: any) => dataset.id === group.dsIdForGroup,
  );
  const [editGroupName, setEditGroupName] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [nameError, setNameError] = useState(false);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setShowPopover(true);
  };

  const handleGroupNameValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length > 40) {
      setNameError(true);
      return;
    } else {
      setNameError(false);
    }

    let isUnique = true;

    Object.keys(chartGroup.groups).forEach((grp) => {
      if (chartGroup.groups[grp].name === value) {
        isUnique = false;
      }
    });

    if (isUnique) {
      dispatch(updateChartFilterGroupsName(group.id, value));
    } else {
      Logger('error', 'Group name should be unique.');
      setSeverity('error');
      setOpenAlert(true);
      setTestMessage('Group name should be unique.');
    }
  };

  const ExpandCollapseIconSwitch = () => {
    return group.isCollapsed ? (
      <Tooltip
        title="Expand"
        arrow
        slotProps={{
          tooltip: {
            sx: { fontSize: '12px' },
          },
        }}
      >
        <ChevronRightIcon
          sx={{
            fontSize: fontSize.doubleExtraLarge,
            color: color,
            marginLeft: 'auto',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            dispatch(updateChartFilterGroupsCollapsed(group.id, !group.isCollapsed));
          }}
        />
      </Tooltip>
    ) : (
      <Tooltip
        title="Collapse"
        arrow
        slotProps={{
          tooltip: {
            sx: { fontSize: '12px' },
          },
        }}
      >
        <KeyboardArrowDownIcon
          sx={{
            fontSize: fontSize.doubleExtraLarge,
            color: color,
            marginLeft: 'auto',
            cursor: 'pointer',
            transform: 'translateX(-1px)',
          }}
          onClick={(e) => {
            dispatch(updateChartFilterGroupsCollapsed(group.id, !group.isCollapsed));
          }}
        />
      </Tooltip>
    );
  };

  const handleReset = () => {
    getAllFilterOptions({
      group,
      dsDetails,
      token,
      dispatch,
      chartGroup,
      dashBoardFilterGroup,
      setLoading,
    });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {editGroupName ? (
        <Tooltip
          title={nameError ? 'Group name cannot exceed 40 characters.' : ''}
          placement="left"
          open={nameError}
          disableHoverListener={!nameError}
          arrow
          slotProps={{ tooltip: { sx: { fontSize: '12px' } } }}
        >
          <input
            autoFocus
            value={group.name}
            onChange={handleGroupNameValue}
            style={{
              height: '1.4rem',
              lineHeight: '1.5rem',
              width: '6rem',
              border: 'none',
              outline: 'none',
              paddingLeft: '10px',
              borderBottom: `2px solid ${nameError ? 'red' : '#2BB9BB'}`,
              margin: '5px',
              fontSize: fontSize.medium,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            onBlur={() => {
              setEditGroupName(false);
            }}
            title="Press enter or click away to save"
          />
        </Tooltip>
      ) : (
        <Tooltip
          title="Double click to rename"
          arrow
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <span
            onDoubleClick={() => tabTileProps.dashMode === 'Edit' && setEditGroupName(true)}
            style={{
              margin: '5px 0',
              fontSize: fontSize.medium,
              color: color,
              maxWidth: '9rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {group.name}
          </span>
        </Tooltip>
      )}
      <Box
        sx={{
          display: 'inline-flex',
          gap: '0.1rem',
          width: '3rem',
          justifyContent: 'flex-end',
          marginRight: '0.2rem',
        }}
      >
        {fromDashboard ? (
          <>
            <Tooltip
              title="Reset"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <RestartAltOutlined
                fontSize="small"
                sx={{
                  color: color,
                  cursor: 'pointer',
                  marginTop: '0.125rem',
                  border: 'none',
                  outline: 'none',
                  backgroundColor: 'transparent',
                }}
                onClick={handleReset}
              />
            </Tooltip>
            <button
              type="button"
              title="More Options"
              style={{
                // width:'fit-content',
                padding: 0,
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
              }}
              className={`${tabTileProps.dashMode !== 'Edit' ? 'hidden' : ''}`}
              onClick={handleClick}
            >
              <MoreVertIcon
                className={`${tabTileProps.dashMode !== 'Edit' ? 'hidden' : ''}`}
                aria-describedby={id}
                style={{
                  fontSize: fontSize.large,
                  color: color,
                  marginRight: '0.2rem',
                }}
                onClick={() => handleClick}
              />
            </button>
          </>
        ) : null}

        {!fromDashboard ? (
          <>
            <Tooltip
              title="Reset"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <RestartAltOutlined
                fontSize="small"
                sx={{
                  color: color,
                  cursor: 'pointer',
                }}
                onClick={handleReset}
              />
            </Tooltip>
            <MoreVertIcon
              onClick={(event) => {
                // @ts-ignore
                setAnchorElSyncFilter(event.currentTarget);
              }}
              style={{
                height: '16px',
                width: '16px',
                color: color,
                marginRight: '0.2rem',
                cursor: 'pointer',
              }}
            />
          </>
        ) : null}
        <div>
          <ExpandCollapseIconSwitch />
        </div>
      </Box>
    </Box>
  );
};

export default GroupHeader;
